import { useEffect, useState } from 'react';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Lee un valor persistido en sessionStorage. Si el valor guardado es un objeto,
 * se mezcla sobre el fallback para tolerar campos nuevos agregados al estado.
 */
export function readSessionStorageValue<T>(key: string, fallback: T): T {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw == null) return fallback;
    const parsed = JSON.parse(raw);
    if (isPlainObject(parsed) && isPlainObject(fallback)) {
      return { ...fallback, ...parsed } as T;
    }
    return parsed as T;
  } catch {
    return fallback;
  }
}

/**
 * Igual que useState, pero persiste el valor en sessionStorage para que
 * sobreviva a la navegación (ej. ir a editar y volver atrás).
 */
export function useSessionStorageState<T>(key: string, defaultValue: T) {
  const [value, setValue] = useState<T>(() => readSessionStorageValue(key, defaultValue));

  useEffect(() => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // sessionStorage no disponible (modo privado, etc.): seguir sin persistir
    }
  }, [key, value]);

  return [value, setValue] as const;
}

export function readLocalStorageValue<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    const parsed = JSON.parse(raw);
    if (isPlainObject(parsed) && isPlainObject(fallback)) {
      return { ...fallback, ...parsed } as T;
    }
    return parsed as T;
  } catch {
    return fallback;
  }
}

/** Persiste en localStorage (sobrevive al cerrar la pestaña). */
export function useLocalStorageState<T>(key: string, defaultValue: T) {
  const [value, setValue] = useState<T>(() => readLocalStorageValue(key, defaultValue));

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* localStorage no disponible */
    }
  }, [key, value]);

  return [value, setValue] as const;
}
